import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { secureGetPdf } from "../../config/api";
import { secureDelete, secureGet, securePut } from "../../config/api";
import { formatDateTime, formatAmount } from "../../utils/format";
import DeletePopup from "../../components/DeletePopup";
import ProductRow from "../../components/ProductRow";
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from "../../components/Breadcrumbs";
import GeneratePopup from "../../components/GeneratePopUp";
import LoadingLogo from "../../components/LoadingLogo";
import DeliveryNotePayments from "./DeliveryNotePayments";

import trashIcon from "../../icons/trashW.svg";
import epxortIcon from "../../icons/export.svg";
import EyeIcon from "../../icons/eyeB.svg";
import paymentsIcon from "../../icons/payments.svg";

const DeliveryNoteDetails = () => {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const { id } = useParams();
  const [deliveryNote, setDeliveryNote] = useState({});
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blPayments, setBlPayments] = useState([]);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);


  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const InvoiceGenerated = deliveryNote.carts?.[0]?.deliveryNote.invoice !== null;

  const fetchBL = useCallback(async () => {
    setLoading(true);
    try {
      const response = await secureGet(`/deliveryNotes/${id}`, showSnackbar, { signal });
      const fetchedBL = response.data;
      setDeliveryNote(fetchedBL);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Failed to fetch order:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id, signal]);

  useEffect(() => {
    fetchBL();

    return () => {
      abortController.abort();
    };
  }, [id, abortController, fetchBL]);

  const quotesExist = deliveryNote.carts?.[0]?.quotes && deliveryNote.carts?.[0]?.quotes.length > 0;
  const invoiceExist = deliveryNote.carts?.[0]?.deliveryNote.invoice && deliveryNote.carts?.[0]?.deliveryNote?.invoice !== null;

  const renderStatut = () => (
    <div className="flex items-center ">
      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${quotesExist ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
          }`}
        onClick={() => quotesExist && handleStatutClick("DV")}
      >
        DV
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>


      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
        onClick={() => handleStatutClick("BC")}
      >
        BC
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className="px-[5px] py-1 rounded-[10px] font-semibold text-[12px] bg-[#0AD69F] text-white cursor-pointer"
      >
        BL
      </span>
      <div className="w-2 h-[2px] bg-[#043F88]"></div>

      <span
        className={`px-[5px] py-1 rounded-[10px] font-semibold text-[12px] ${invoiceExist ? "bg-[#0AD69F] text-white cursor-pointer" : "bg-[#EF476F] text-white cursor-not-allowed"
          }`}
        onClick={() => invoiceExist && handleStatutClick("FA")}
      >
        FA
      </span>
    </div>
  );

  const handleStatutClick = (key) => {
    switch (key) {
      case "DV":
        handleConsultDV();
        break;
      case "BC":
        handleConsultBC();
        break;
      case "FA":
        handleConsultFA();
        break;
      default:
        break;
    }
  };

  const handleConsultDV = () => {
    history.push({
      pathname: `/quotes/details/${deliveryNote.carts?.[0]?.id}`,
    });
  };

  const handleConsultBC = () => {
    history.push({
      pathname: `/orders/details/${deliveryNote.carts?.[0]?.id}`,
    });
  };

  const handleConsultFA = () => {
    history.push({
      pathname: `/invoices/details/${deliveryNote.carts?.[0]?.deliveryNote?.invoice?.id}`,
    });
  };

  const handleDeleteClick = () => {
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await secureDelete(`/deliveryNotes/delete/${deliveryNote.id}`, showSnackbar);
      setDeletePopupVisible(false);
      history.push({
        pathname: "/delivery-notes",
        state: {
          showSnackbar: true,
          snackbarMessage: t(`confirmation.deliveryNotesDelete`),
          snackbarSeverity: 'success',
          filters: location.state?.filters || {},
          page: location.state?.page || 0,
          size: location.state?.size || 30,
        },
      });   
     } catch (error) {
      setDeletePopupVisible(false);
      console.error("Error deleting the delivery note:", error);
    }
  };

  const handleDownloadClick = async () => {
    try {
      const response = await secureGetPdf(`/deliveryNotes/${deliveryNote.id}/pdf`, showSnackbar);

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Bon_Livraison_${deliveryNote.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      showSnackbar(t('confirmation.downloadSuccess'), 'success');

    } catch (error) {
      console.error("Failed to download BL:", error);
    }
  };

  const handleGenerateFAClick = () => {
    console.log("Generating FA for BL:", deliveryNote.id);
    setPopupOpen(true);
  };

  const handleConfirm = async () => {
    console.log("User confirmed FA generation.");

    try {
      await securePut("invoices/add", { deliveryNoteId: deliveryNote.id }, showSnackbar);
      showSnackbar(t(`confirmation.orderInvoiceGenerated`), 'success');
      fetchBL();
    } catch (error) {
      console.error("Failed to generate FA:", error);
    } finally {
      setPopupOpen(false);
    }
  };

  const handleCancel = () => {
    console.log("User canceled FA generation.");
    setPopupOpen(false);
  };


  const BlPayments = () => {
    setBlPayments(deliveryNote.deliveryNotePayments);
    setIsPaymentsModalOpen(true);
  };

  const renderActionButtons = () => {
    return (
      <>
        <button
          className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
          onClick={() => handleDownloadClick()}
        >
          <img src={epxortIcon} alt="delete" className="w-5 h-5" />
          {t(`actions.downloadBL`)}
        </button>
        {!InvoiceGenerated && (
          <button
            className="flex items-center justify-center bg-white text-[#043F88] font-bold text-[14px] px-6 py-3 rounded-[12px] shadow-[0px_20px_80px_#00000011] gap-x-2"
            onClick={handleGenerateFAClick}
          >
            <img src={EyeIcon} alt="icon" className="w-5 h-5" />
            {t("actions.generateInvoice")}
          </button>
        )}

        <button
          className="flex items-center justify-center bg-white text-[#043F88] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
          onClick={() => BlPayments()}
        >
          <img src={paymentsIcon} alt="payments" className="w-5 h-5" />
          {t(`titles.payments`)}
        </button>

        <button
          className="flex items-center justify-center bg-[#EF476F] text-[#FFFFFF] px-6 py-3 font-bold text-[14px] rounded-[12px] gap-x-2 shadow-[0px_20px_80px_#00000011]"
          onClick={handleDeleteClick}>
          <img src={trashIcon} alt="" className="w-5 h-5" />

          {t(`actions.delete`)}
        </button>
      </>
    );
  };

  if (loading) return <LoadingLogo />;

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.delivery_note_details")}</title>
      </Helmet>
      <div className="mb-4">
        <Breadcrumbs />
        <h1 className="text-[40px] font-semibold text-[#000000]">
          {t(`titles.delivery_note_details`)}
        </h1>

        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-[10px]">
            <p className="text-[#00000] font-semibold text-[24px]">
              {deliveryNote.deliveryNoteNumber}
            </p>

            <div className="flex space-x-1">{renderStatut()}</div>
          </div>

          <div className="flex space-x-4">
            {renderActionButtons(deliveryNote.id)}
          </div>
        </div>

        <div>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {deliveryNote.client?.code} - {deliveryNote.client?.name}
          </p>
        </div>
      </div>

      <div className="w-full flex justify-between bg-white py-4 pl-[32px] rounded-[12px] shadow-[0px_6px_26px_#626B7D12] mb-4">
        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t(`rows.creationDate`)}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatDateTime(deliveryNote.createdAt)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t(`rows.commercial`)}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {deliveryNote.user?.firstName} {deliveryNote.user?.lastName}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">STOCK</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {deliveryNote.carts?.[0]?.repo?.name}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">
            {t(`rows.paymentMode`)}
          </p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {deliveryNote.carts?.[0]?.paymentMode}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL HT</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(deliveryNote.priceHT)}
          </p>
        </div>

        <div className="flex-1">
          <p className="text-[#707070] text-[14px] font-semibold mb-1">TOTAL TTC</p>
          <p className="text-[#38485C] text-[20px] font-semibold">
            {formatAmount(deliveryNote.priceTTC)}
          </p>
        </div>
      </div>


      <h3 className="text-[16px] font-medium text-[#ACAFC6] mb-4">
        {t(`fields_types.list`)}
      </h3>

      <div className="space-y-2">
        {deliveryNote.carts?.[0]?.details.map((product, index) => (
          <ProductRow
            key={index}
            product={product}
          />
        ))}
      </div>

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
        />
      )}

      {/* Generate FA Popup */}
      <GeneratePopup
        open={isPopupOpen}
        entity={t(`entity.fa`)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      />
      {isPaymentsModalOpen && (
        <DeliveryNotePayments
          blPayments={blPayments}
          onClose={() => setIsPaymentsModalOpen(false)}
        />
      )}
    </div>
  );
};

export default DeliveryNoteDetails;
